import { Pilot } from '../types';

const PILOT_KEY = 'rf_pilot_cache';

/**
 * Last pilot record resolved for the signed-in user, kept in localStorage.
 *
 * The PocketBase auth store persists the *user*, but the pilot is fetched on
 * every init — so without this cache an offline start has a session and no
 * pilot, and every screen guarded by `useAuthGuard` reports "você precisa ser
 * um piloto registrado".
 */
export function readCachedPilot(userId: string): Pilot | null {
  try {
    const raw = localStorage.getItem(PILOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { userId: string; pilot: Pilot };
    if (parsed.userId !== userId) return null;
    return parsed.pilot ?? null;
  } catch {
    return null;
  }
}

export function writeCachedPilot(userId: string, pilot: Pilot | null): void {
  try {
    if (!pilot) {
      localStorage.removeItem(PILOT_KEY);
      return;
    }
    localStorage.setItem(PILOT_KEY, JSON.stringify({ userId, pilot }));
  } catch {
    // Storage full or unavailable (private mode): the cache is best-effort.
  }
}

export function clearCachedPilot(): void {
  try {
    localStorage.removeItem(PILOT_KEY);
  } catch {
    // ignore
  }
}

/**
 * True when the failure is "we could not reach the server", not "the server
 * rejected this session". PocketBase reports network/abort failures as
 * status 0; only 401/403 mean the token is actually dead.
 */
export function isNetworkFailure(error: unknown): boolean {
  const status = (error as { status?: number } | null)?.status;
  if (status === 401 || status === 403) return false;
  if (typeof status === 'number' && status >= 400) return false;
  return true;
}
